import {
  getOptions,
  getSelectedAddons,
  getVariant,
  updatePriceElements,
  updateLinkedProductPrices,
} from "product-utils";

const SELECTORS = {
  checkbox: ".grafikr-product__upsell-card__checkbox-input",
  checkboxLabel: ".grafikr-product__upsell-card__checkbox-label",
  inner: ".grafikr-product__upsell-card__inner",
  productData: "[data-data]",
};

if (!customElements.get("upsell-card")) {
  customElements.define(
    "upsell-card",
    class UpsellCard extends HTMLElement {
      connectedCallback() {
        this.productRoot = this.closest("[data-template-product]");
        this.checkbox = this.querySelector(SELECTORS.checkbox);
        this.checkboxLabel = this.querySelector(SELECTORS.checkboxLabel);
        this.inner = this.querySelector(SELECTORS.inner);

        if (!this.productRoot || !this.checkbox) {
          console.info("Upsell card is missing its product root or checkbox");
          return;
        }

        this.productData = this.#getProductData();
        if (!this.productData) {
          console.info("Upsell card product data could not be parsed");
          return;
        }

        this.handleCheckboxChange = this.handleCheckboxChange.bind(this);
        this.handlePricesUpdated = this.handlePricesUpdated.bind(this);
        this.handleLabelKeydown = this.handleLabelKeydown.bind(this);

        this.checkbox.addEventListener("change", this.handleCheckboxChange);
        this.checkboxLabel?.addEventListener("keydown", this.handleLabelKeydown);
        document.addEventListener("product:pricesUpdated", this.handlePricesUpdated);
        this.#setSelectedState(this.isSelected);
        this.#updateUpsellState(this.isSelected);

        this.#syncDisplayedPrices();
      }

      disconnectedCallback() {
        this.checkbox?.removeEventListener("change", this.handleCheckboxChange);
        this.checkboxLabel?.removeEventListener("keydown", this.handleLabelKeydown);
        document.removeEventListener("product:pricesUpdated", this.handlePricesUpdated);
      }

      handleLabelKeydown(event) {
        if (event.key !== "Enter" && event.key !== " ") return;
        event.preventDefault();
        this.checkbox?.click();
      }

      handleCheckboxChange() {
        if (this.dataset.upsellAvailable !== "true") {
          this.#setSelectedState(this.isSelected);
          return;
        }

        const selected = this.checkbox.checked;
        const nextValue = selected ? this.enabledValue : this.disabledValue;
        const didSwitchVariant = this.#selectUpsellOptionValue(nextValue);

        if (!didSwitchVariant) {
          this.#setSelectedState(!selected);
          return;
        }

        this.#setSelectedState(selected);
        this.#updateUpsellState(selected);
        this.#syncDisplayedPrices();
        this.#dispatchUpsellChanged(selected);
      }

      handlePricesUpdated() {
        updateLinkedProductPrices(this.productRoot, this.isSelected);
      }

      #dispatchUpsellChanged(selected) {
        document.dispatchEvent(
          new CustomEvent("product:upsellChanged", {
            detail: {
              selected,
              productId: this.dataset.productId,
              upsellProductId: this.dataset.upsellProductId,
              upsellVariantId: this.dataset.upsellVariantId,
              upsellPrice: selected ? this.upsellPrice : 0,
            },
            bubbles: true,
          })
        );
      }

      #getProductData() {
        const dataElement = this.productRoot?.querySelector(SELECTORS.productData);
        if (!dataElement?.textContent) return null;

        try {
          return JSON.parse(dataElement.textContent);
        } catch (error) {
          console.error("Error parsing upsell card product data:", error);
          return null;
        }
      }

      #getCurrentVariant() {
        if (!this.productData) return null;

        const selectedOptions = getOptions(this.productRoot, this.productData);
        return (
          getVariant(this.productRoot, this.productData, selectedOptions) ||
          this.productData.selected_or_first_available_variant ||
          null
        );
      }

      #updateUpsellState(selected) {
        window.grafikrProductUpsellState = {
          selectedUpsell: selected
            ? {
                id: this.dataset.upsellEnabledVariantId,
                productId: this.dataset.productId,
                title: this.dataset.upsellTitle || "",
                price: this.dataset.upsellPrice,
              }
            : null,
        };
      }

      #setSelectedState(selected) {
        this.dataset.upsellSelected = String(selected);
        this.checkbox.checked = selected;
        this.inner?.classList.toggle("grafikr-product__upsell-card__inner--selected", selected);
      }

      #syncDisplayedPrices() {
        const currentVariant = this.#getCurrentVariant();
        if (currentVariant) {
          updatePriceElements(this.productRoot, currentVariant, getSelectedAddons());
        }

        updateLinkedProductPrices(this.productRoot, this.isSelected);
      }

      #selectUpsellOptionValue(nextValue) {
        if (Number.isNaN(this.upsellOptionIndex)) return false;

        const optionInputs = Array.from(
          this.productRoot.querySelectorAll(`[data-option="${this.upsellOptionIndex}"]`)
        );

        const targetInput = optionInputs.find((input) => {
          return this.#normalizeValue(input.value) === this.#normalizeValue(nextValue);
        });

        if (!targetInput || targetInput.disabled) {
          return false;
        }

        if (targetInput.checked) {
          return true;
        }

        targetInput.checked = true;
        targetInput.dispatchEvent(new Event("change", { bubbles: true }));
        return true;
      }

      #normalizeValue(value) {
        return String(value || "").trim().toLowerCase();
      }

      get upsellPrice() {
        return parseInt(this.dataset.upsellPrice || 0, 10);
      }

      get upsellOptionIndex() {
        return parseInt(this.dataset.upsellOptionIndex || "", 10);
      }

      get enabledValue() {
        return this.dataset.upsellEnabledValue || "";
      }

      get disabledValue() {
        return this.dataset.upsellDisabledValue || "";
      }

      get isSelected() {
        return this.dataset.upsellSelected === "true";
      }
    }
  );
}
